import React, { useState } from 'react';
import { Button, Gap, Gutter, Icon, Text } from '@common/components';
import { Colors } from '@common/tokens';
import type { ScheduleReviewNeedsAttentionType } from '@load-balancing/graphql/queries/schedule-review';
import {
  needsAttentionDismissId,
  needsAttentionPanelState,
} from '@load-balancing/services';

// Severity → dot color. Unknown severities fall back to the medium (amber) tone.
const DOT_COLOR: Record<ScheduleReviewNeedsAttentionType['severity'], string> =
  {
    HIGH: Colors.coral,
    MEDIUM: Colors.harvestGold,
  };

// Action → button label. The buttons are presentational for now (not wired).
const ACTION_LABEL: Record<ScheduleReviewNeedsAttentionType['action'], string> =
  {
    VIEW_IN_TEAM: 'View in Team',
    SHOW_ON_SCHEDULE: 'Show on Schedule',
  };

/**
 * Query-based "Needs Attention" cards for the review panel, split into two
 * groups: this-week alerts (charge/shift gaps, fatigue, off-unit placement) and
 * schedule-period alerts (FTE attainment — the same on every week), week-first.
 * Each card can be dismissed for the session: week-scoped cards are dismissed
 * per week (keyed by `weekKey`) so dismissing one week's flag doesn't hide a
 * different week's same-named flag; period-scoped cards (identical across weeks)
 * are dismissed globally. Shows a positive affirmation when nothing is left to
 * flag — the queries succeeded with no flags, or the user dismissed them all —
 * and renders nothing only when the data is unavailable (the analyzer errored,
 * or it hasn't loaded — null).
 *
 * All markup is kept inline (not split into child components) so the scoped
 * `<style jsx>` below applies to the cards — styled-jsx only scopes elements
 * rendered within this component.
 */
export function ScheduleNeedsAttention({
  items,
  // Stable key of the viewed week; namespaces week-scoped dismissals so they
  // don't leak across weeks.
  weekKey,
  // Date-range label of the viewed week, e.g. "Jul 13 – 19", shown on the
  // week-scoped section so the manager knows which dates the flags cover.
  weekLabel,
  // Invoked when a card's action button is clicked (e.g. an FTE card opening the
  // Team tab pre-filtered). The parent decides what each action does.
  onAction,
  // Dismiss-id of the card whose shifts are currently shown on the grid (owned by
  // the parent); the matching card's button reads "Hide on Schedule".
  shownScheduleKey,
  // Invoked when a card is dismissed, so the parent can clear a matching grid
  // highlight; runs alongside the local session dismissal.
  onDismiss,
}: {
  items?: ReadonlyArray<ScheduleReviewNeedsAttentionType> | null;
  weekKey?: string;
  weekLabel?: string;
  onAction?: (item: ScheduleReviewNeedsAttentionType) => void;
  shownScheduleKey?: string | null;
  onDismiss?: (item: ScheduleReviewNeedsAttentionType) => void;
}) {
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);

  // Only null/undefined items (analyzer errored server-side, or not yet loaded) hide
  // the panel; both an empty server list and a fully-dismissed list earn the positive
  // affirmation. See needsAttentionPanelState.
  const state = needsAttentionPanelState(items, dismissedIds, weekKey);
  if (state.kind === 'hidden') {
    return null;
  }
  if (state.kind === 'empty') {
    return (
      <Gutter asPadding direction="vertical" size="xl">
        <Gap vertical align="center" size="xs">
          <Icon icon="circleCheck" kind="solid" size="xl" color="grassMid" />
          <Text bold size="md" align="center">
            Your schedule is looking good
          </Text>
          <Text secondary size="sm" align="center">
            No items need your attention right now.
          </Text>
        </Gap>
      </Gutter>
    );
  }
  const visible = state.visible;

  const dismiss = (id: string) => setDismissedIds((ids) => [...ids, id]);

  const sections = [
    {
      label: weekLabel ? `This week · ${weekLabel}` : 'This week',
      items: visible.filter((item) => item.scope === 'WEEK'),
    },
    {
      label: 'This schedule period',
      items: visible.filter((item) => item.scope === 'PERIOD'),
    },
  ].filter((section) => section.items.length > 0);

  return (
    <div className="schedule-needs-attention">
      <Text weight="medium" size="lg">
        Needs Attention
      </Text>

      {sections.map((section) => (
        <div key={section.label} className="schedule-needs-attention__section">
          <Text size="sm" secondary transform="uppercase">
            {section.label}
          </Text>
          <div className="schedule-needs-attention__cards">
            <Gap vertical size="xs" align="stretch">
              {section.items.map((item) => {
                // True when THIS card's shifts are the ones currently ringed on the
                // grid; flips its action button to "Hide on Schedule".
                const isShownOnSchedule =
                  item.action === 'SHOW_ON_SCHEDULE' &&
                  shownScheduleKey === needsAttentionDismissId(item, weekKey);
                return (
                  <div
                    key={item.key}
                    className="schedule-needs-attention__card"
                  >
                    <div className="schedule-needs-attention__row">
                      <div className="schedule-needs-attention__content">
                        <span
                          className="schedule-needs-attention__dot"
                          style={{
                            backgroundColor:
                              DOT_COLOR[item.severity] ?? DOT_COLOR.MEDIUM,
                          }}
                        />
                        <div className="schedule-needs-attention__text">
                          <Text
                            weight="medium"
                            size="md"
                            block
                            color="midnightRegular"
                            className="schedule-needs-attention__card-title"
                          >
                            {item.title}
                          </Text>
                          {item.detail ? (
                            <Text secondary size="md" block>
                              {item.detail}
                            </Text>
                          ) : null}
                        </div>
                      </div>
                      <div className="schedule-needs-attention__actions">
                        <Button
                          kind={isShownOnSchedule ? 'primary' : 'secondary'}
                          size="sm"
                          onClick={() => onAction?.(item)}
                        >
                          {isShownOnSchedule
                            ? 'Hide on Schedule'
                            : (ACTION_LABEL[item.action] ?? 'View')}
                        </Button>
                        <button
                          type="button"
                          className="schedule-needs-attention__dismiss"
                          aria-label="Dismiss alert"
                          onClick={() => {
                            onDismiss?.(item);
                            dismiss(needsAttentionDismissId(item, weekKey));
                          }}
                        >
                          <Icon icon="xMark" kind="light" size="sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Gap>
          </div>
        </div>
      ))}

      <style jsx>{`
        .schedule-needs-attention__section {
          margin-top: 24px;
        }
        .schedule-needs-attention__cards {
          margin-top: 8px;
        }
        .schedule-needs-attention__card {
          border: 1px solid ${Colors.steelLighter};
          border-radius: 4px;
          padding: 16px;
          background-color: ${Colors.white};
        }
        /* Full-width row; align-items:flex-start keeps the actions column
           pinned to the top of the card when content wraps to multiple lines. */
        .schedule-needs-attention__row {
          display: flex;
          width: 100%;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }
        /* Grows to fill the row; min-width:0 lets the detail wrap instead of
           pushing the actions off the edge. */
        .schedule-needs-attention__content {
          display: flex;
          flex: 1;
          min-width: 0;
          align-items: flex-start;
          gap: 8px;
        }
        .schedule-needs-attention__actions {
          flex-shrink: 0;
          display: flex;
          align-items: flex-start;
          gap: 6px;
        }
        .schedule-needs-attention__dot {
          flex-shrink: 0;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          margin-top: 6px;
        }
        .schedule-needs-attention__card-title {
          margin-bottom: 2px;
        }
        .schedule-needs-attention__dismiss {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 2px 0 0 2px;
          border: 0;
          background: transparent;
          color: ${Colors.midnightLighter};
          cursor: pointer;
          border-radius: 4px;
        }
        .schedule-needs-attention__dismiss:hover {
          color: ${Colors.midnightRegular};
          background-color: ${Colors.steelLightest};
        }
      `}</style>
    </div>
  );
}
